/* eslint-disable lingui/no-unlocalized-strings -- U02: backend enum values; all user-facing labels are translated. */
import { useLingui } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Check } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { walletByCodeQuery } from '@/modules/wallet/public';
import { useConfig } from '@/shared/config';
import { usePermissions } from '@/shared/permissions';
import { formatCurrencyInCents } from '@/shared/http';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { FlowPanel } from '@/shared/flow';
import { BillingCreateFields } from './billing-create-fields';
import { BillingParameterFields } from './billing-parameter-fields';
import { useBillingMutation, billingKeys } from './billing.queries';
import { notifySuccess, useNotifier } from '@/shared/data';
import { getBillingTags } from './billing.api';
import { pendingPrincipal } from './billing.blocks';
import {
  validBillingGuarantor,
  billingCreateBody,
  billingParameters,
  billingParametersBody,
  billingPixEnabled,
  billingMethods,
  validBillingCreate,
  validBillingParameters,
  type BillingCreateValues,
  type BillingPaymentMethod,
} from './billing-workflow.model';
import type { Billing } from './billing.wire';
const initial: BillingCreateValues = {
  walletCode: '',
  personId: '',
  documentNumber: '',
  description: null,
  dueDate: '',
  paymentLimitDate: null,
  amount: null,
  payer: null,
};
export function BillingWorkflowDialog({
  row,
  onClose,
  onCreated,
}: {
  row?: Billing;
  onClose: () => void;
  onCreated: (id: string, record: Billing) => void;
}) {
  const lock = useRef(false);
  const formId = useId();
  const { t } = useLingui();
  const notifier = useNotifier();
  const config = useConfig();
  const permissions = usePermissions();
  const [values, setValues] = useState<BillingCreateValues>(initial);
  const [record, setRecord] = useState<Billing | undefined>(row);
  const [step, setStep] = useState(row ? 2 : 0);
  const [idempotence] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [confirmWithout, setConfirmWithout] = useState(false);
  const create = useBillingMutation('create');
  const patch = useBillingMutation('workflowPatch');
  const register = useBillingMutation('register');
  const [methods, setMethods] = useState<BillingPaymentMethod[]>(['BankSlip']);
  const [selectedTags, setSelectedTags] = useState<string[] | undefined>();
  const [tagSearch, setTagSearch] = useState('');
  const [newTag, setNewTag] = useState('');
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [parameters, setParameters] = useState(() =>
    row
      ? billingParameters(row)
      : billingParameters({
          id: '',
          walletCode: 0,
          amount: 0,
          registered: false,
          statusValue: 'NotRegistered',
        }),
  );
  const [guarantor, setGuarantor] = useState({
    name: row?.guarantor?.name ?? '',
    registrationNumber: row?.guarantor?.registrationNumber ?? '',
  });
  const walletCode = record?.walletCode ?? Number(values.walletCode);
  const wallet = useQuery({
    ...walletByCodeQuery(walletCode),
    enabled: step >= 2 && walletCode > 0,
  });
  const tags = useQuery({
    queryKey: billingKeys.custom('tags', { walletCode, searchString: tagSearch }),
    queryFn: ({ signal }) =>
      getBillingTags({ walletCode, ...(tagSearch ? { searchString: tagSearch } : {}) }, signal),
    enabled: step === 2 && walletCode > 0,
  });
  const allowed =
    permissions.can('BillingAccount', 'Read') &&
    (!row || (!row.registered && row.statusValue !== 'Registering')) &&
    (!record || (!record.registered && record.statusValue !== 'Registering'));
  const pixEnabled = billingPixEnabled(config, walletCode);
  const selectedMethods = billingMethods(methods, pixEnabled);
  const running = busy || create.isPending || patch.isPending || register.isPending;
  const amount =
    record?.statusValue === 'PartialPaid'
      ? pendingPrincipal(record)
      : (record?.amount ?? values.amount ?? 0);
  const titles = [
    t`Nova cobrança`,
    t`Resumo da cobrança`,
    t`Criar cobrança`,
    t`Parâmetros da cobrança`,
    t`Resumo para registro`,
  ];
  const perform = async (fn: () => Promise<void>) => {
    if (!allowed || running || lock.current) return;
    lock.current = true;
    setFailed(false);
    setBusy(true);
    try {
      await fn();
    } catch {
      setFailed(true);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const createNow = (close: boolean) =>
    void perform(async () => {
      const saved =
        record ?? (await create.mutateAsync({ body: billingCreateBody(values), idempotence }));
      setRecord(saved);
      setParameters(billingParameters(saved));
      setGuarantor({
        name: saved.guarantor?.name ?? '',
        registrationNumber: saved.guarantor?.registrationNumber ?? '',
      });
      onCreated(saved.id, saved);
      if (close) onClose();
      else setStep(2);
    });
  const registerNow = () =>
    void perform(async () => {
      if (!record) return;
      await register.mutateAsync({ id: record.id, paymentMethods: selectedMethods });
      onClose();
    });
  const next = () => {
    if (!allowed || running) return;
    if (step === 0) {
      if (!validBillingCreate(values)) {
        setFailed(true);
        return;
      }
      setFailed(false);
      setStep(1);
      return;
    }
    if (step === 1) {
      createNow(false);
      return;
    }
    if (!record) return;
    if (step === 2) {
      if (!wallet.data || wallet.isError) return;
      void perform(async () => {
        await patch.mutateAsync({ id: record.id, body: { tags: selectedTags } });
        notifySuccess(notifier, { title: msg`Sucesso`, description: msg`Agora você pode definir os parâmetros da cobrança` });
        setStep(3);
      });
      return;
    }
    if (step === 3) {
      if (!validBillingParameters(parameters)) {
        setFailed(true);
        return;
      }
      void perform(async () => {
        await patch.mutateAsync({ id: record.id, body: billingParametersBody(parameters) });
        notifySuccess(notifier, { title: msg`Sucesso`, description: msg`Parâmetros da cobrança definidos, agora você pode seguir em frente` });
        setStep(4);
      });
      return;
    }
    if (!record.guarantor?.name && !record.guarantor?.registrationNumber) {
      setConfirmWithout(true);
      return;
    }
    registerNow();
  };
  if (!allowed) return null;
  const back =
    step > 0 && step !== 2
      ? () => {
          setFailed(false);
          setStep(step - 1);
        }
      : undefined;
  return (
    <FlowPanel
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={titles[step]}
      description={t`Confira cada etapa. A criação e o registro só serão enviados ao confirmar.`}
      steps={titles.map((title, index) => ({ id: String(index), title }))}
      current={String(step)}
      onBack={back}
      busy={running}
      size="md"
      slot="billing-workflow"
      actions={
        <>
          {step === 1 && (
            <Button
              type="button"
              variant="secondary"
              disabled={running}
              onClick={() => createNow(true)}
            >{t`Salvar sem cobrar agora`}</Button>
          )}
          <Button
            type="submit"
            form={formId}
            loading={running}
            disabled={confirmWithout || (step === 2 && (!wallet.data || wallet.isError))}
          >
            {step === 4 ? <Check aria-hidden /> : <ArrowRight aria-hidden />}
            {step === 4
              ? t`Registrar cobrança`
              : step === 1
                ? t`Criar e continuar`
                : t`Continuar`}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          next();
        }}
      >
        <fieldset disabled={running} className="grid gap-4">
          {step === 0 && (
            <BillingCreateFields values={values} onChange={setValues} onBusy={setBusy} />
          )}
          {step === 1 && (
            <fieldset className="grid gap-2">
              <legend>{t`Confira os dados`}</legend>
              <p>
                {t`Carteira`}: {values.walletCodeDisplay ?? values.walletCode}
              </p>
              <p>
                {t`Pagador`}: {values.payer?.name}
              </p>
              <p>
                {t`Número do documento`}: {values.documentNumber}
              </p>
              <p>
                {t`Descrição`}: {values.description}
              </p>
              <p>
                {t`Email`}: {values.payer?.email}
              </p>
              <p>
                {t`Endereço`}: {values.payer?.addressName}
              </p>
              <p>
                {t`Data de vencimento`}: {values.dueDate}
              </p>
              <p>
                {t`Data limite de pagamento`}: {values.paymentLimitDate ?? t`N/D`}
              </p>
              <p>
                {t`Valor da cobrança`}: {formatCurrencyInCents(values.amount ?? 0)}
              </p>
            </fieldset>
          )}
          {step >= 2 && (
            <p className="rounded-md bg-muted p-3">
              {record?.statusValue === 'PartialPaid'
                ? t`Valor pendente da cobrança`
                : t`Valor da cobrança`}
              : {formatCurrencyInCents(amount)}
            </p>
          )}
          {step === 2 && (
            <>
              <fieldset className="grid gap-2">
                <legend>{t`Categorias`}</legend>
                <label>
                  {t`Buscar categoria`}
                  <Input value={tagSearch} onChange={(e) => setTagSearch(e.target.value)} />
                </label>
                {tags.isError && (
                  <p role="alert">{t`Não foi possível carregar as categorias.`}</p>
                )}
                {[...new Set([...(tags.data ?? []), ...localTags])].map((tag) => (
                  <label key={tag} className="flex gap-2">
                    <input
                      type="checkbox"
                      checked={selectedTags?.includes(tag) ?? false}
                      onChange={(event) =>
                        setSelectedTags(
                          event.target.checked
                            ? [...(selectedTags ?? []), tag]
                            : (selectedTags ?? []).filter((item) => item !== tag),
                        )
                      }
                    />
                    {tag}
                  </label>
                ))}
                <label>
                  {t`Nova categoria`}
                  <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!newTag.trim()}
                  onClick={() => {
                    setLocalTags([...localTags, newTag]);
                    setSelectedTags([...(selectedTags ?? []), newTag]);
                    setNewTag('');
                  }}
                >{t`Adicionar categoria`}</Button>
              </fieldset>
              <fieldset className="grid gap-2">
                <legend>{t`Como você quer cobrar?`}</legend>
                {wallet.data && !wallet.isError ? (
                  <>
                    {(['BankSlip', 'Pix'] as const).map((method) => (
                      <label key={method} className="flex gap-2">
                        <input
                          type="checkbox"
                          disabled={method === 'Pix' && !pixEnabled}
                          checked={selectedMethods.includes(method)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...selectedMethods, method]
                              : selectedMethods.filter((item) => item !== method);
                            if (next.length) setMethods(billingMethods(next, pixEnabled));
                          }}
                        />
                        {method === 'Pix' ? t`Pix` : t`Boleto bancário`} ·{' '}
                        {formatCurrencyInCents(
                          (method === 'Pix'
                            ? wallet.data?.bankSlipCosts?.pixRegister
                            : wallet.data?.bankSlipCosts?.register) ?? 0,
                        )}
                        {method === 'Pix' && !pixEnabled && (
                          <span>{t`Indisponível para esta carteira`}</span>
                        )}
                      </label>
                    ))}
                    <p>
                      {t`Link de pagamento`}: {t`Em breve`}
                    </p>
                    <p>
                      {t`Total de emissão`}:{' '}
                      {formatCurrencyInCents(
                        selectedMethods.reduce(
                          (total, method) =>
                            total +
                            ((method === 'Pix'
                              ? wallet.data?.bankSlipCosts?.pixRegister
                              : wallet.data?.bankSlipCosts?.register) ?? 0),
                          0,
                        ),
                      )}
                    </p>
                  </>
                ) : (
                  <p role="status">{t`Tarifas da carteira indisponíveis. Aguarde ou tente novamente.`}</p>
                )}
                {wallet.isError && (
                  <p role="alert">{t`Não foi possível consultar as tarifas da carteira.`}</p>
                )}
              </fieldset>
            </>
          )}
          {step === 3 && (
            <BillingParameterFields
              value={parameters}
              onChange={setParameters}
              walletCode={walletCode}
              onBusy={setBusy}
              onError={() => setFailed(true)}
            />
          )}
          {step === 4 && (
            <>
              <p>
                {t`Número do documento`}: {record?.documentNumber}
              </p>
              <p>
                {t`Pagador`}: {record?.payer?.name}
              </p>
              <p>
                {t`Data de vencimento`}: {parameters.dueDate.slice(0, 10)}
              </p>
              <p>
                {t`Desconto`}: {formatCurrencyInCents(Number(parameters.discount ?? 0))}
              </p>
              <p>
                {t`Meios de cobrança`}:{' '}
                {selectedMethods
                  .map((method) => (method === 'Pix' ? t`Pix` : t`Boleto bancário`))
                  .join(', ')}
              </p>
              <fieldset className="grid gap-2">
                <legend>{t`Sacador avalista`}</legend>
                <label>
                  {t`Nome`}
                  <Input
                    value={guarantor.name}
                    onChange={(e) => setGuarantor({ ...guarantor, name: e.target.value })}
                  />
                </label>
                <label>
                  {t`Documento do avalista`}
                  <Input
                    value={guarantor.registrationNumber}
                    onChange={(e) =>
                      setGuarantor({ ...guarantor, registrationNumber: e.target.value })
                    }
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={running}
                  onClick={() => {
                    if (
                      !guarantor.name ||
                      !validBillingGuarantor(guarantor.registrationNumber, config)
                    ) {
                      setFailed(true);
                      return;
                    }
                    void perform(async () => {
                      if (!record) return;
                      const saved = {
                        name: guarantor.name,
                        registrationNumber: guarantor.registrationNumber.replace(
                          /[^a-zA-Z0-9]/g,
                          '',
                        ),
                      };
                      await patch.mutateAsync({ id: record.id, body: { guarantor: saved } });
                      setRecord({ ...record, guarantor: saved });
                    });
                  }}
                >{t`Salvar sacador avalista`}</Button>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={running}
                  onClick={() =>
                    void perform(async () => {
                      if (!record) return;
                      const empty = { name: '', registrationNumber: '' };
                      await patch.mutateAsync({ id: record.id, body: { guarantor: empty } });
                      setRecord({ ...record, guarantor: empty });
                      setGuarantor(empty);
                    })
                  }
                >{t`Remover sacador avalista`}</Button>
              </fieldset>
            </>
          )}
        </fieldset>
        {failed && (
          <p role="alert">{t`Não foi possível concluir esta etapa. Confira os campos e tente novamente; as etapas concluídas foram preservadas.`}</p>
        )}
        {confirmWithout && (
          <div role="alert" className="rounded-md border p-3">
            <p>{t`Deseja registrar sem sacador avalista?`}</p>
            <Button
              type="button"
              disabled={running}
              onClick={registerNow}
            >{t`Sim, registrar sem sacador avalista`}</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmWithout(false)}
            >{t`Adicionar sacador avalista`}</Button>
          </div>
        )}
      </form>
    </FlowPanel>
  );
}
